import { Routes, Route, Navigate } from "react-router-dom";
import RouteGuard from "./components/RouteGuard.jsx";
import LoginPage from "./pages/LoginPage.jsx";
import ForgotPasswordPage from "./pages/ForgotPasswordPage.jsx";
import ResetPasswordPage from "./pages/ResetPasswordPage.jsx";
import AdminLayout from "./pages/admin/AdminLayout.jsx";
import AdminUsersPage from "./pages/admin/AdminUsersPage.jsx";
import AdminReportsPage from "./pages/admin/AdminReportsPage.jsx";
import AdminStatsPage from "./pages/admin/AdminStatsPage.jsx";
import DashboardLayout from "./dashboard/Layout.jsx";
import { DashboardContent } from "./components/dashboard/dashboard-content";
import { AuditSectionContent } from "./components/audit/audit-section-content";
import { ProfileContent } from "./components/profile/profile-content";
import { useState, useEffect } from "react";
import { useDispatch, useStore } from "react-redux";
import { useAuth } from "./hooks/use-auth";
import { useTheme } from "./contexts/theme-context";
import { Toaster } from "../src/components/ui/toaster";
import { SyncStatusWidget } from "./components/shared/sync-status-widget";
import { InstallPrompt } from "./components/shared/install-prompt";
import { startSyncManager } from "./lib/syncManager.js";
import { requestPersistentStorage } from "./lib/offlineDb.js";
import { hydratePendingFileUploads } from "./redux/slices/fileSlice.js";


export default function App() {
  const [selectedVersion, setSelectedVersion] = useState(null);
  const { user, isLoading } = useAuth();
  const { setTheme } = useTheme();
  const dispatch = useDispatch();
  const store = useStore();



  useEffect(() => {
    setTheme(user?.applicationPreferences?.theme);
  }, [user?.applicationPreferences?.theme, setTheme]);

  // Phase 2: start draining the offline outbox as soon as the app mounts,
  // and keep listening for reconnect events for the lifetime of the app.
  useEffect(() => {
    const stopSyncManager = startSyncManager(dispatch, store.getState, store.subscribe);
    requestPersistentStorage();
    // Phase 3: re-render any offline-uploaded images that are still sitting
    // in the outbox with a fresh blob URL (the previous one died on reload).
    dispatch(hydratePendingFileUploads());
    return stopSyncManager;
  }, [dispatch, store, user?._id, user?.id, isLoading]);

  return (
    <>
      <Toaster />
      <SyncStatusWidget />
      <InstallPrompt />
      <Routes>
        <Route path="/" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/dashboard"
          element={
            <RouteGuard roles={["auditor", "manager", "admin"]}>
              <DashboardLayout
                children={
                  <DashboardContent
                    selectedVersion={selectedVersion}
                    setSelectedVersion={setSelectedVersion}

                  />
                }
              />
            </RouteGuard>
          }
        />
        <Route
          path="/admin"
          element={
            <RouteGuard roles={["admin"]}>
              <AdminLayout />
            </RouteGuard>
          }
        >
          <Route index element={<Navigate to="users" replace />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="reports" element={<AdminReportsPage />} />
          <Route path="stats" element={<AdminStatsPage />} />
        </Route>
        <Route
          path="/audit/:siteId/:reportId/:section"
          element={
            <RouteGuard roles={["auditor", "manager", "admin"]}>
              <AuditSectionContent

              />
            </RouteGuard>
          }
        />
        <Route
          path="/audit/:siteId/:section"
          element={<Navigate to="/dashboard" replace />}
        />

        <Route
          path="/profile"
          element={
            <RouteGuard roles={["auditor", "manager", "admin"]}>
              <ProfileContent />
            </RouteGuard>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
